/**
 * Website Integration component
 * Handles website integration with DIY guides and remote access options
 */

import React, { useEffect, useState, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';

import { Input } from '@/components/ui/input';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from '@/components/ui/form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Laptop, Code, Eye, EyeOff, CheckCircle, AlertCircle, Wrench, Users, Info, Copy, Settings, ChevronRight, Mail, ChevronDown, Save } from 'lucide-react';

import { useOnboarding } from '@/hooks/useOnboarding';
import { OnboardingSection, OnboardingStatus, SetupMethod, WebsiteIntegrationFormData } from '@/types/onboarding';
import { toast } from '@/hooks/use-toast';

// Validation schema
const websiteIntegrationSchema = z.object({
  website_setup_method: z.nativeEnum(SetupMethod),
  website_login_username: z.string().optional(),
  website_login_password: z.string().optional(),
  website_platform: z.string().optional(),
  website_platform_custom: z.string().optional(),
}).refine((data) => {
  if (data.website_setup_method === SetupMethod.REMOTE_ACCESS) {
    return data.website_login_username && data.website_login_password;
  }
  return true;
}, {
  message: "Website login credentials are required for remote access setup",
  path: ["website_login_username"],
});

type WebsiteIntegrationFormValues = z.infer<typeof websiteIntegrationSchema>;

interface WebsiteIntegrationProps {
  onNavigateToSection?: (section: string) => void;
}

const WebsiteIntegration: React.FC<WebsiteIntegrationProps> = ({ onNavigateToSection }) => {
  const {
    onboardingData,
    getSectionData,
    getSectionStatus,
    validateSection,
    updateSection,
    markSectionComplete,
    isSaving,
    isMarkingComplete,
  } = useOnboarding();

  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isSavingAuto, setIsSavingAuto] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [diySubTab, setDiySubTab] = useState('edit-website');
  const [showEmailContent, setShowEmailContent] = useState(false);
  const [emailButtonOption, setEmailButtonOption] = useState<'add-new' | 'edit-existing'>('add-new');
  const [diyButtonOption, setDiyButtonOption] = useState<'add-new' | 'edit-existing'>('add-new');

  const sectionStatus = getSectionStatus(OnboardingSection.WEBSITE);
  const sectionData = useMemo(() => getSectionData(OnboardingSection.WEBSITE), [onboardingData]);
  const currentSetupMethod = sectionData?.website_setup_method || SetupMethod.DIY;

  const form = useForm<WebsiteIntegrationFormValues>({
    resolver: zodResolver(websiteIntegrationSchema),
    defaultValues: {
      website_setup_method: SetupMethod.DIY,
      website_login_username: '',
      website_login_password: '',
      website_platform: '',
      website_platform_custom: '',
    },
  });

  // Watch the form's current setup method for tab state
  const activeSetupMethod = form.watch('website_setup_method');

  // Load existing data
  useEffect(() => {
    if (sectionData) {
      form.reset({
        website_setup_method: sectionData.website_setup_method || SetupMethod.DIY,
        website_login_username: sectionData.website_login_username || '',
        website_login_password: sectionData.website_login_password || '',
        website_platform: sectionData.website_platform || '',
        website_platform_custom: sectionData.website_platform_custom || '',
      });
      setHasUnsavedChanges(false);
    }
  }, [sectionData, form.reset]);

  // Auto-save functionality
  let autoSaveTimeout: NodeJS.Timeout;
  
  useEffect(() => {
    const subscription = form.watch(() => {
      setHasUnsavedChanges(true);
      
      // Clear existing timeout
      if (autoSaveTimeout) {
        clearTimeout(autoSaveTimeout);
      }
      
      // Set new timeout for auto-save
      autoSaveTimeout = setTimeout(async () => {
        if (hasUnsavedChanges) {
          setIsSavingAuto(true);
          try {
            const formData = form.getValues();
            await updateSection(OnboardingSection.WEBSITE, formData);
            setHasUnsavedChanges(false);
            setLastSaved(new Date());
          } catch (error) {
            console.error('Auto-save failed:', error);
          } finally {
            setIsSavingAuto(false);
          }
        }
      }, 2000);
    });

    return () => {
      subscription.unsubscribe();
      if (autoSaveTimeout) {
        clearTimeout(autoSaveTimeout);
      }
    };
  }, [autoSaveTimeout]);

  // Handle setup method change
  const handleSetupMethodChange = (method: SetupMethod) => {
    form.setValue('website_setup_method', method);
    setHasUnsavedChanges(true);
  };

  // Save section data
  const handleSave = async () => {
    try {
      const formData = form.getValues();
      await updateSection(OnboardingSection.WEBSITE, formData);
      setHasUnsavedChanges(false);
      setLastSaved(new Date());
    } catch (error) {
      console.error('Error saving website integration:', error);
    }
  };

  // Mark section as complete
  const handleMarkComplete = async () => {
    try {
      const formData = form.getValues();
      await updateSection(OnboardingSection.WEBSITE, formData);
      await markSectionComplete(OnboardingSection.WEBSITE);
      setHasUnsavedChanges(false);
    } catch (error) {
      console.error('Error marking website integration complete:', error);
    }
  };

  // Check if can mark complete
  const canMarkComplete = () => {
    const values = form.getValues();
    if (values.website_setup_method === SetupMethod.REMOTE_ACCESS) {
      return !!(values.website_login_username && values.website_login_password);
    }
    return true; // DIY can always be marked complete
  };

  // Check if domain step 1 is completed
  const isDomainStep1Completed = () => {
    const domainData = getSectionData(OnboardingSection.DOMAIN);
    return !!(domainData?.root_domain && domainData?.custom_sub_domain);
  };

  // Generate developer email for website integration
  const generateDeveloperEmail = () => {
    if (!isDomainStep1Completed()) {
      setShowEmailContent(!showEmailContent);
      return;
    }
    setShowEmailContent(!showEmailContent);
  };

  // Get ordering URL from domain setup
  const getOrderingUrl = () => {
    const domainData = getSectionData(OnboardingSection.DOMAIN);
    if (domainData?.root_domain && domainData?.custom_sub_domain) {
      return `https://${domainData.custom_sub_domain}.${domainData.root_domain}`;
    }
    return '[Your Ordering URL]';
  };

  // Copyable Email Content component
  const CopyableEmailContent = ({ title, content }: { title: string; content: string }) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = async () => {
      try {
        await navigator.clipboard.writeText(content);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.error('Failed to copy:', err);
      }
    };

    return (
      <div className="bg-gray-50 p-4 rounded border">
        <div className="flex items-center justify-between mb-2">
          <h5 className="font-medium text-sm">{title}</h5>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={handleCopy}
            className="h-8 px-3"
          >
            {copied ? (
              <>
                <CheckCircle className="h-4 w-4 mr-1 text-green-600" />
                Copied!
              </>
            ) : (
              <>
                <Copy className="h-4 w-4 mr-1" />
                Copy
              </>
            )}
          </Button>
        </div>
        <pre className="text-sm whitespace-pre-wrap bg-white p-3 rounded border font-mono">
          {content}
        </pre>
      </div>
    );
  };

  // DIY instructions with choice structure  
  const diyInstructions = [
    {
      type: 'choice_header',
      number: '1',
      text: "Choose ONE of the following options:"
    },
    {
      type: 'choice_option',
      number: '1a',
      text: "Edit website yourself",
      tabLink: 'edit-website'
    },
    {
      type: 'choice_option', 
      number: '1b',
      text: "Send instructions to your web developer",
      tabLink: 'send-to-developer'
    }
  ];

  // Status badge component
  const StatusBadge = () => {
    switch (sectionStatus) {
      case OnboardingStatus.COMPLETE:
        return <Badge className="bg-green-100 text-green-800">Complete</Badge>;
      case OnboardingStatus.AWAITING_CONFIRMATION:
        return <Badge className="bg-yellow-100 text-yellow-800">Under Review</Badge>;
      case OnboardingStatus.NEEDS_ATTENTION:
        return <Badge className="bg-red-100 text-red-800">Needs Attention</Badge>;
      default:
        return <Badge variant="secondary">Not Started</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Laptop className="h-6 w-6 text-primary" />
          <div>
            <h2 className="text-xl font-semibold">Website Integration</h2>
            <p className="text-sm text-muted-foreground">
              Add an "Order Online" button to your existing website
            </p>
          </div>
        </div>
        <StatusBadge />
      </div>

      {/* Status alerts */}
      {sectionStatus === OnboardingStatus.NEEDS_ATTENTION && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            This section needs your attention. Please review the feedback from our team and make the necessary changes.
          </AlertDescription>
        </Alert>
      )}

      {sectionStatus === OnboardingStatus.AWAITING_CONFIRMATION && (
        <Alert>
          <CheckCircle className="h-4 w-4" />
          <AlertDescription>
            Your website integration has been submitted for review. Our team will verify the setup and contact you if any changes are needed.
          </AlertDescription>
        </Alert>
      )}

      {sectionStatus === OnboardingStatus.COMPLETE && (
        <Alert className="border-green-200 bg-green-50">
          <CheckCircle className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-700">
            Your website integration has been successfully set up and verified. Customers can now access your ordering system from your website.
          </AlertDescription>
        </Alert>
      )}

      <div className="space-y-6">
        <Card>
          <CardContent className="p-6">
            <Tabs value={activeSetupMethod} onValueChange={(value) => handleSetupMethodChange(value as SetupMethod)} className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value={SetupMethod.DIY} className="flex items-center gap-2 data-[state=active]:text-white group">
                  <Wrench className="h-4 w-4 group-data-[state=active]:text-white group-data-[state=active]:stroke-white" />
                  DIY Setup
                </TabsTrigger>
                <TabsTrigger value={SetupMethod.REMOTE_ACCESS} className="flex items-center gap-2 data-[state=active]:text-white group">
                  <Users className="h-4 w-4 group-data-[state=active]:text-white group-data-[state=active]:stroke-white" />
                  Remote Access
                </TabsTrigger>
              </TabsList>

              <TabsContent value={SetupMethod.DIY} className="space-y-6 mt-6">
                <div className="space-y-4">
                  <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg">
                    <Wrench className="h-5 w-5 text-blue-600 mt-1" />
                    <div>
                      <h4 className="font-medium text-blue-900">Do It Yourself</h4>
                      <p className="text-sm text-blue-700">
                        Follow our step-by-step guide to add an "Order Online" button to your website yourself. You'll have full control over the process.
                      </p>
                    </div>
                  </div>

                  {/* DIY Instructions */}
                  <div className="space-y-6">
                    {diyInstructions.map((instruction, index) => {
                      if (instruction.type === 'choice_header') {
                        return (
                          <div key={index} className="flex items-center gap-4">
                            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary text-white text-xs font-semibold flex items-center justify-center">{instruction.number}</div>
                            <div className="flex-1">
                              <div className="text-sm font-medium">{instruction.text}</div>
                            </div>
                          </div>
                        );
                      }
                      
                      if (instruction.type === 'choice_option') {
                        const isActive = diySubTab === instruction.tabLink;
                        return (
                          <div key={index}>
                            <div 
                              className={`flex items-center gap-4 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                                isActive 
                                  ? 'border-green-300 bg-green-50 shadow-sm' 
                                  : 'border-gray-200 bg-gray-50 hover:border-green-200'
                              }`}
                              onClick={() => setDiySubTab(instruction.tabLink)}
                            >
                              <div className={`flex-shrink-0 w-6 h-6 rounded-full text-white text-xs flex items-center justify-center ${
                                isActive ? 'bg-brand-green' : 'bg-gray-400'
                              }`}>
                                {instruction.number}
                              </div>
                              <div className="flex-1">
                                <div className={`text-sm ${isActive ? 'text-green-600 font-medium' : 'text-gray-500 font-muted-background'}`}>
                                  {instruction.text}
                                </div>
                              </div>
                              <ChevronRight className={`h-4 w-4 ${isActive ? 'text-primary rotate-90' : 'text-gray-400'}`} />
                            </div>
                          </div>
                        );
                      }

                      return null;
                    })}
                  </div>

                  {/* DIY Subtabs */}
                  <Tabs value={diySubTab} onValueChange={setDiySubTab} className="space-y-4">
                    <TabsList className="grid w-full grid-cols-2">
                      <TabsTrigger value="edit-website" className="group">
                        <Wrench className="h-4 w-4 mr-2 group-data-[state=active]:stroke-white" />
                        Edit website yourself
                      </TabsTrigger>
                      <TabsTrigger value="send-to-developer" className="group">
                        <Mail className="h-4 w-4 mr-2 group-data-[state=active]:stroke-white" />
                        Send email to developer
                      </TabsTrigger>
                    </TabsList>

                    {/* Edit Website Tab */}
                    <TabsContent value="edit-website" className="space-y-4 pb-6">
                      <div className="space-y-4">
                        <p className="text-sm text-muted-foreground">
                          Follow these platform-specific instructions to add an "Order Online" button to your website using your website builder's editor.
                        </p>

                        <div className="space-y-4">
                          <div className="bg-blue-50 p-4 rounded border">
                            <h5 className="font-medium mb-3 text-blue-900">Your Ordering URL:</h5>
                            <div className="bg-white p-2 rounded border font-mono text-sm break-all">
                              {getOrderingUrl()}
                            </div>
                          </div>

                          {/* DIY Button Option Selection */}
                          <div className="space-y-3 p-4 bg-gray-50 border rounded-lg">
                            <h5 className="font-medium text-sm">What would you like to do?</h5>
                            <RadioGroup value={diyButtonOption} onValueChange={(value) => setDiyButtonOption(value as 'add-new' | 'edit-existing')}>
                              <div className="flex items-center space-x-2">
                                <RadioGroupItem value="add-new" id="diy-add-new" />
                                <Label htmlFor="diy-add-new" className="text-sm cursor-pointer">Add a new "Order Online" button</Label>
                              </div>
                              <div className="flex items-center space-x-2">
                                <RadioGroupItem value="edit-existing" id="diy-edit-existing" />
                                <Label htmlFor="diy-edit-existing" className="text-sm cursor-pointer">Edit existing order/delivery buttons</Label>
                              </div>
                            </RadioGroup>
                          </div>

                          {/* Platform Instructions */}
                          <div className="space-y-4">
                            <h5 className="font-medium">Platform-Specific Instructions:</h5>
                            
                            <Accordion type="single" collapsible className="w-full">
                              <AccordionItem value="wordpress">
                                <AccordionTrigger className="text-sm font-medium">WordPress</AccordionTrigger>
                                <AccordionContent>
                                  <ol className="text-sm text-muted-foreground space-y-1 list-decimal list-inside ml-2">
                                    {diyButtonOption === 'add-new' ? (
                                      <>
                                        <li>Log into your WordPress admin dashboard</li>
                                        <li>Go to Pages → Edit the page where you want the button</li>
                                        <li>Click the "+" to add a new block and select "Button"</li>
                                        <li>Type "Order Online" as the button text</li>
                                        <li>Click the button, then click the link icon in the toolbar</li>
                                        <li>Paste your ordering URL and toggle "Open in new tab"</li>
                                        <li>Update the page</li>
                                      </>
                                    ) : (
                                      <>
                                        <li>Log into your WordPress admin dashboard</li>
                                        <li>Go to Pages/Posts and edit pages with existing order buttons</li>
                                        <li>Find existing order/delivery buttons on your pages</li>
                                        <li>Click on each button to select it</li>
                                        <li>Click the link icon in the toolbar to edit the link</li>
                                        <li>Replace the old URL with your new ordering URL</li>
                                        <li>Ensure "Open in new tab" is enabled and update the page</li>
                                      </>
                                    )}
                                  </ol>
                                </AccordionContent>
                              </AccordionItem>

                              <AccordionItem value="squarespace">
                                <AccordionTrigger className="text-sm font-medium">Squarespace</AccordionTrigger>
                                <AccordionContent>
                                  <ol className="text-sm text-muted-foreground space-y-1 list-decimal list-inside ml-2">
                                    {diyButtonOption === 'add-new' ? (
                                      <>
                                        <li>Log into your Squarespace account and enter Edit mode</li>
                                        <li>Navigate to the page where you want the button</li>
                                        <li>Click "+" to add content and select "Button"</li>
                                        <li>Type "Order Online" as the button text</li>
                                        <li>In the button settings, set Link to "URL"</li>
                                        <li>Paste your ordering URL and check "Open in new window"</li>
                                        <li>Save and publish your changes</li>
                                      </>
                                    ) : (
                                      <>
                                        <li>Log into your Squarespace account and enter Edit mode</li>
                                        <li>Navigate through your pages to find existing order buttons</li>
                                        <li>Click on each existing order/delivery button</li>
                                        <li>In the button settings, update the Link URL</li>
                                        <li>Replace with your new ordering URL</li>
                                        <li>Ensure "Open in new window" is checked</li>
                                        <li>Save and publish your changes</li>
                                      </>
                                    )}
                                  </ol>
                                </AccordionContent>
                              </AccordionItem>

                              <AccordionItem value="wix">
                                <AccordionTrigger className="text-sm font-medium">Wix</AccordionTrigger>
                                <AccordionContent>
                                  <ol className="text-sm text-muted-foreground space-y-1 list-decimal list-inside ml-2">
                                    {diyButtonOption === 'add-new' ? (
                                      <>
                                        <li>Log into Wix and open your site editor</li>
                                        <li>Click "Add" on the left panel, then select "Button"</li>
                                        <li>Drag the button to your desired location</li>
                                        <li>Double-click the button to edit text to "Order Online"</li>
                                        <li>Click "Change Link" and select "Web address"</li>
                                        <li>Paste your ordering URL and enable "Open link in new window"</li>
                                        <li>Publish your site</li>
                                      </>
                                    ) : (
                                      <>
                                        <li>Log into Wix and open your site editor</li>
                                        <li>Browse through your pages to locate existing order buttons</li>
                                        <li>Click on each existing order/delivery button to select it</li>
                                        <li>Click "Change Link" in the button settings</li>
                                        <li>Update the web address to your new ordering URL</li>
                                        <li>Ensure "Open link in new window" is enabled</li>
                                        <li>Publish your site</li>
                                      </>
                                    )}
                                  </ol>
                                </AccordionContent>
                              </AccordionItem>

                              <AccordionItem value="webflow">
                                <AccordionTrigger className="text-sm font-medium">Webflow</AccordionTrigger>
                                <AccordionContent>
                                  <ol className="text-sm text-muted-foreground space-y-1 list-decimal list-inside ml-2">
                                    {diyButtonOption === 'add-new' ? (
                                      <>
                                        <li>Log into your Webflow account and open your project</li>
                                        <li>Navigate to the page where you want the button</li>
                                        <li>Drag a "Button" element from the Elements panel</li>
                                        <li>Double-click the button to edit text to "Order Online"</li>
                                        <li>With the button selected, go to Settings panel</li>
                                        <li>Set Link Type to "URL" and paste your ordering URL</li>
                                        <li>Check "Open in new tab" and publish your site</li>
                                      </>
                                    ) : (
                                      <>
                                        <li>Log into your Webflow account and open your project</li>
                                        <li>Navigate through your pages to find existing order buttons</li>
                                        <li>Click on each existing order/delivery button to select it</li>
                                        <li>In the Settings panel, update the Link URL</li>
                                        <li>Replace with your new ordering URL</li>
                                        <li>Ensure "Open in new tab" is checked</li>
                                        <li>Publish your site</li>
                                      </>
                                    )}
                                  </ol>
                                </AccordionContent>
                              </AccordionItem>
                            </Accordion>
                          </div>
                        </div>

                        <Alert>
                          <Info className="h-4 w-4" />
                          <AlertDescription>
                            These instructions work for most website builders. If your platform isn't listed or you need help, consider using the "Send email to developer" option instead.
                          </AlertDescription>
                        </Alert>

                        {/* What happens next for Edit Website */}
                        <div className="space-y-3 pb-4">
                          <h4 className="font-medium">What happens next:</h4>
                          <div className="space-y-4 text-sm text-muted-foreground">
                            <div className="flex items-center gap-4">
                              <div className="flex-shrink-0 w-6 h-6 rounded-full bg-green-500 text-white text-xs flex items-center justify-center">
                                <CheckCircle className="h-4 w-4 stroke-white" />
                              </div>
                              <div className="flex-1">
                                <span>Your ordering page will become accessible from your website</span>
                              </div>
                            </div>
                            <div className="flex items-center gap-4">
                              <div className="flex-shrink-0 w-6 h-6 rounded-full bg-green-500 text-white text-xs flex items-center justify-center cursor-pointer hover:bg-green-600 transition-colors" onClick={() => { onNavigateToSection?.(OnboardingSection.GOOGLE_BUSINESS); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>
                                <ChevronRight className="h-4 w-4 stroke-white" />
                              </div>
                              <div className="flex-1">
                                <span>Proceed to Google integration to let customers access this page from your Google Business Profile! <a href="#" onClick={(e) => { e.preventDefault(); onNavigateToSection?.(OnboardingSection.GOOGLE_BUSINESS); window.scrollTo({ top: 0, behavior: 'smooth' }); }} className="text-primary hover:underline">Go to Google Business Profile Integration</a></span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </TabsContent>

                    {/* Send to Developer Tab */}
                    <TabsContent value="send-to-developer" className="space-y-4 pb-6">
                      <div className="space-y-4">
                        <div className="border rounded-lg p-4">
                          <h4 className="font-medium mb-2">Send Setup Instructions to Your Developer</h4>
                          <p className="text-sm text-muted-foreground mb-4">
                            We'll prepare a detailed email with instructions for adding an "Order Online" button to your website.
                          </p>
                          <Button 
                            onClick={generateDeveloperEmail}
                            className="flex items-center gap-2"
                          >
                            <Mail className="h-4 w-4" />
                            {showEmailContent ? (
                              <>
                                <ChevronDown className="h-4 w-4" />
                                Hide Email Content
                              </>
                            ) : (
                              'Generate Developer Email'
                            )}
                          </Button>
                        </div>

                        {showEmailContent && (
                          <>
                            {isDomainStep1Completed() ? (
                              <>
                                {/* Email Button Option Selection */}
                                <div className="border rounded-lg p-4">
                                  <h5 className="font-medium text-sm mb-3">What would you like to do?</h5>
                                  <RadioGroup value={emailButtonOption} onValueChange={(value) => setEmailButtonOption(value as 'add-new' | 'edit-existing')}>
                                    <div className="flex items-center space-x-2">
                                      <RadioGroupItem value="add-new" id="email-add-new" />
                                      <Label htmlFor="email-add-new" className="text-sm cursor-pointer">Add a new "Order Online" button</Label>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                      <RadioGroupItem value="edit-existing" id="email-edit-existing" />
                                      <Label htmlFor="email-edit-existing" className="text-sm cursor-pointer">Edit existing order/delivery buttons</Label>
                                    </div>
                                  </RadioGroup>
                                </div>

                                <div className="space-y-4">
                                  <CopyableEmailContent 
                                    title="Email Subject"
                                    content="Action Required: Add Order Online Button to Website"
                                  />
                                  <CopyableEmailContent 
                                    title="Email Body"
                                    content={emailButtonOption === 'add-new' ? 
                                      `Hi,

I hope you're doing well. I need your assistance with adding an "Order Online" button to our website to integrate with our new ordering system.

Please add the following button to our website:

Ordering URL: ${getOrderingUrl()}

Instructions:
1. Log into our website builder/editor (WordPress, Squarespace, Wix, Shopify, etc.)
2. Add an "Order Online" button to the main navigation menu
3. Add "Order Online" buttons to prominent locations on relevant pages (homepage, menu page, etc.)
4. Set all button links to: ${getOrderingUrl()}
5. Configure buttons to open in a new tab/window
6. Style the button to match our website's design and branding

Platform-specific steps:
• WordPress: Add a button block or menu item, set the link URL and "Open in new tab" option
• Squarespace: Use a button block, set the link URL and select "Open in new window"
• Wix: Add a button element, set the link to "Web address" and enable "Open link in new window"
• Shopify: Add a button in the theme editor, set the URL and "Open in new window" option

This integration will allow customers to seamlessly access our online ordering system directly from our website.

If you need any clarification or run into issues with your specific website platform, please let me know.

Thank you!

Best regards,
${onboardingData?.contact_person || '[Your Name]'}` :
                                      `Hi,

I hope you're doing well. I need your assistance with updating the existing order/delivery buttons on our website to integrate with our new ordering system.

Please update the following:

New Ordering URL: ${getOrderingUrl()}

Instructions:
1. Log into our website builder/editor (WordPress, Squarespace, Wix, Shopify, etc.)
2. Find ALL existing order, delivery, or "order now" buttons on our website
3. Edit each button to change the link to: ${getOrderingUrl()}
4. Ensure all buttons are set to open in a new tab/window
5. Keep the existing button text and styling - just update the link URL
6. Check all pages including: homepage, menu page, contact page, and any other relevant pages

Common button locations to check:
- Main navigation menu
- Homepage hero section
- Menu pages
- Footer sections
- Contact/location pages
- Any promotional banners or call-to-action sections

Platform-specific steps:
• WordPress: Edit button blocks or menu items, update the link URL and ensure "Open in new tab" is selected
• Squarespace: Click on buttons to edit, change the link URL and verify "Open in new window" is enabled
• Wix: Select button elements, change the link destination to "Web address" and enable "Open link in new window"
• Shopify: Access theme editor, update button URLs and enable "Open in new window" option

This update will redirect all existing order buttons to our new ordering system without changing the website's design.

Please confirm once all buttons have been updated. If you encounter any issues with your specific website platform or need help finding certain buttons, please let me know.

Thank you!

Best regards,
${onboardingData?.contact_person || '[Your Name]'}`}
                                  />
                                </div>
                              </>
                            ) : (
                              <Alert variant="destructive" className="text-white">
                                <AlertCircle className="h-4 w-4 stroke-white" />
                                <AlertDescription className="text-white">
                                  Please complete Step 1 of the Domain Setup first to generate the ordering URL needed for this email. 
                                  <a href="#" onClick={(e) => { e.preventDefault(); onNavigateToSection?.(OnboardingSection.DOMAIN); window.scrollTo({ top: 0, behavior: 'smooth' }); }} className="text-white hover:text-gray-200 underline ml-1">Go to Domain Setup</a>
                                </AlertDescription>
                              </Alert>
                            )}
                          </>
                        )}
                      </div>

                      {/* What happens next for Send to Developer */}
                      <div className="space-y-3 pb-4">
                        <h4 className="font-medium">What happens next:</h4>
                        <div className="space-y-4 text-sm text-muted-foreground">
                          <div className="flex items-center gap-4">
                            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-green-500 text-white text-xs flex items-center justify-center">
                              <CheckCircle className="h-4 w-4 stroke-white" />
                            </div>
                            <div className="flex-1">
                              <span>Your ordering page will become accessible from your website</span>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-green-500 text-white text-xs flex items-center justify-center cursor-pointer hover:bg-green-600 transition-colors" onClick={() => { onNavigateToSection?.(OnboardingSection.GOOGLE_BUSINESS); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>
                              <ChevronRight className="h-4 w-4 stroke-white" />
                            </div>
                            <div className="flex-1">
                              <span>Proceed to Google integration to let customers access this page from your Google Business Profile! <a href="#" onClick={(e) => { e.preventDefault(); onNavigateToSection?.(OnboardingSection.GOOGLE_BUSINESS); window.scrollTo({ top: 0, behavior: 'smooth' }); }} className="text-primary hover:underline">Go to Google Business Profile Integration</a></span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </TabsContent>
                  </Tabs>

                </div>
              </TabsContent>

              <TabsContent value={SetupMethod.REMOTE_ACCESS} className="space-y-6 mt-6">
                <div className="space-y-6">
                  <div className="flex items-start gap-3 p-4 bg-green-50 rounded-lg">
                    <Users className="h-5 w-5 text-green-600 mt-1" />
                    <div>
                      <h4 className="font-medium text-green-900">Remote Access Setup</h4>
                      <p className="text-sm text-green-700">
                        Our team will add a professionally styled "Order Online" button to your website, ensuring it matches your branding and works perfectly across all devices.
                      </p>
                    </div>
                  </div>

                  {/* Remote Access Form */}
                  <Form {...form}>
                    <div className="space-y-4 p-4 bg-gray-50 rounded border">
                      <h4 className="font-medium">Website Access Details</h4>
                      
                      <FormField
                        control={form.control}
                        name="website_platform"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Website Platform</FormLabel>
                            <Select onValueChange={field.onChange} value={field.value}>
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="Select your website platform" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value="squarespace">Squarespace</SelectItem>
                                <SelectItem value="wix">Wix</SelectItem>
                                <SelectItem value="wordpress">WordPress</SelectItem>
                                <SelectItem value="webflow">Webflow</SelectItem>
                                <SelectItem value="custom">Custom/Other</SelectItem>
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      {form.watch('website_platform') === 'custom' && (
                        <FormField
                          control={form.control}
                          name="website_platform_custom"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Please specify your platform</FormLabel>
                              <FormControl>
                                <Input {...field} placeholder="e.g., Drupal, Joomla, Custom HTML" />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      )}

                      <FormField
                        control={form.control}
                        name="website_login_username"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Website Admin Username</FormLabel>
                            <FormControl>
                              <Input {...field} placeholder="Your website admin username" />
                            </FormControl>
                            <FormDescription>
                              We need admin access to add the order button to your website
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="website_login_password"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Website Admin Password</FormLabel>
                            <FormControl>
                              <div className="relative">
                                <Input 
                                  {...field} 
                                  type={showPassword ? "text" : "password"}
                                  placeholder="Your website admin password" 
                                />
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                                  onClick={() => setShowPassword(!showPassword)}
                                >
                                  {showPassword ? (
                                    <EyeOff className="h-4 w-4" />
                                  ) : (
                                    <Eye className="h-4 w-4" />
                                  )}
                                </Button>
                              </div>
                            </FormControl>
                            <FormDescription>
                              Your credentials are encrypted and will be removed after setup completion
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </Form>

                  <Alert>
                    <Info className="h-4 w-4" />
                    <AlertDescription>
                      We recommend changing your password after the setup is complete for added security.
                    </AlertDescription>
                  </Alert>
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        {/* Status indicators */}
        {isSavingAuto && (
          <div className="flex items-center gap-2 text-sm text-blue-600">
            <div className="animate-spin h-4 w-4 border-2 border-blue-600 border-t-transparent rounded-full"></div>
            Saving...
          </div>
        )}
        
        {!isSavingAuto && hasUnsavedChanges && activeSetupMethod === SetupMethod.REMOTE_ACCESS && (
          <div className="flex items-center gap-2 text-sm text-amber-600">
            <AlertCircle className="h-4 w-4" />
            You have unsaved changes
          </div>
        )}
        
        {lastSaved && !hasUnsavedChanges && (
          <div className="flex items-center gap-2 text-sm text-green-600">
            <CheckCircle className="h-4 w-4" />
            Saved {lastSaved.toLocaleTimeString()}
          </div>
        )}

        {/* Action buttons */}
        <div className="flex items-center justify-end gap-3 pt-4">
          {activeSetupMethod === SetupMethod.REMOTE_ACCESS && (
            <Button
              variant="outline"
              onClick={handleSave}
              disabled={isSaving}
              className="flex items-center gap-2"
            >
              <Save className="h-4 w-4" />
              Save Progress
            </Button>
          )}
          <Button
            onClick={handleMarkComplete}
            disabled={!canMarkComplete() || isMarkingComplete}
            variant={sectionStatus === OnboardingStatus.COMPLETE ? "secondary" : "default"}
            className="flex items-center gap-2"
          >
            <CheckCircle className="h-4 w-4" />
            {isMarkingComplete ? 'Submitting...' : 
             sectionStatus === OnboardingStatus.COMPLETE ? 'Completed' : 'Mark as Complete'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default WebsiteIntegration;